import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandInfo, faqs } from '@/mocks/data';
import { useState, FormEvent } from 'react';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于水蜜桃视频',
  description: brandInfo.description,
  url: 'https://teadaye.cn/about',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9tbtpvn8bpodh48r6r0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse failure
      }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string } }).meta?.message
          || (parsed as { meta?: { message?: string; detail?: string } }).meta?.detail
          || (parsed as { message?: string }).message
          || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormStatus('success');
          form.reset();
          return;
        }
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 水蜜桃视频 | 品牌介绍与联系方式"
        description={`${brandInfo.description} 了解水蜜桃视频的品牌故事、发展历程、团队介绍以及联系方式。`}
        keywords="水蜜桃视频,关于我们,品牌介绍,联系水蜜桃视频,水蜜桃平台"
        canonicalUrl="/about"
        jsonLd={[aboutJsonLd, faqJsonLd]}
      />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Peaceful%20peach%20orchard%20landscape%20at%20sunrise%20with%20golden%20morning%20light%2C%20misty%20distant%20hills%2C%20rows%20of%20healthy%20peach%20trees%2C%20serene%20and%20inspiring%20agricultural%20scenery%2C%20warm%20and%20hopeful%20atmosphere%2C%20professional%20landscape%20photography&width=1800&height=600&seq=about-hero&orientation=landscape"
            alt="关于水蜜桃视频"
            title="水蜜桃视频 - 关于我们"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 to-black/40 flex items-center justify-center">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
              关于我们
            </h1>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                品牌故事
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {brandInfo.name}
              </h2>
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-3">
                {brandInfo.description}
              </p>
              <p className="text-foreground-600 text-sm leading-relaxed">
                创立于{brandInfo.founded}，团队规模{brandInfo.teamSize}，现已积累{brandInfo.videoCount}优质短视频内容，服务{brandInfo.usersCount}用户，与{brandInfo.partnersCount}家果园建立了深度合作关系。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
              {[
                { label: '创立时间', value: brandInfo.founded, icon: 'ri-calendar-line' },
                { label: '视频内容', value: brandInfo.videoCount, icon: 'ri-film-line' },
                { label: '服务用户', value: brandInfo.usersCount, icon: 'ri-user-heart-line' },
                { label: '合作果园', value: brandInfo.partnersCount, icon: 'ri-store-2-line' },
              ].map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <div className="text-2xl font-bold text-foreground-900 font-heading mb-1">{stat.value}</div>
                  <div className="text-foreground-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                他们这样说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {brandInfo.testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-foreground-900 text-sm">{t.name}</div>
                      <div className="text-foreground-500 text-xs">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed">{t.content}</p>
                  <div className="flex gap-1 mt-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4">
                常见问题
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                常见问题解答
              </h2>
            </div>
            <div className="max-w-2xl mx-auto flex flex-col gap-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-xl p-5 cursor-pointer">
                  <summary className="flex items-center justify-between font-medium text-foreground-900 text-sm md:text-base list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform text-lg"></i>
                  </summary>
                  <p className="mt-3 text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                取得联系
              </h2>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 max-w-5xl mx-auto">
              <div className="lg:w-2/5 flex flex-col gap-5">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: brandInfo.contact.email },
                  { icon: 'ri-phone-line', label: '联系电话', value: brandInfo.contact.phone },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: brandInfo.contact.address },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: brandInfo.contact.wechat },
                  { icon: 'ri-weibo-line', label: '官方微博', value: brandInfo.contact.weibo },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 bg-background-50 rounded-xl p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                      <div className="text-sm text-foreground-900 font-medium">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="lg:w-3/5 bg-background-50 rounded-xl p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（限500字）"
                    ></textarea>
                  </div>
                  <div className="spam-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="px-8 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap self-start"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600">消息已成功发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}